import {
    ProductClusterType,
    SourceStatus
} from './domain';

export interface SearchRankingScoreComponents {
    visualScore: number;
    categoryScore: number;
    subcategoryScore: number;
    brandScore: number;
    textScore: number;
    sellerPatternScore: number;
    priceEvidenceScore: number;
}

export interface SearchRankingEvidenceInput extends SearchRankingScoreComponents {
    requestedSection: ProductClusterType;
    categoryMatched: boolean;
    subcategoryMatched: boolean | null;
    hasBrandEvidence: boolean;
    hasTextEvidence: boolean;
    hasSellerPatternEvidence: boolean;
    hasPriceEvidence: boolean;
    sourceStatus: SourceStatus;
}

export interface SearchRankingResult extends SearchRankingScoreComponents {
    finalScore: number;
    section: ProductClusterType;
    sourceStatus: SourceStatus;
    needsVerification: boolean;
    supportingEvidenceCount: number;
    downgradeReasons: string[];
}

export const searchRankingScoreWeights: SearchRankingScoreComponents = {
    visualScore: 0.4,
    categoryScore: 0.18,
    subcategoryScore: 0.12,
    brandScore: 0.1,
    textScore: 0.08,
    sellerPatternScore: 0.07,
    priceEvidenceScore: 0.05
};

// 40단계의 점수 가중치와 임계값은 아직 설계 초안이다.
// 실제 카탈로그 평가 데이터의 precision/recall, section purity, nDCG로 보정할 수 있게 상수화한다.
export const searchRankingThresholds = {
    verificationMinFinalScore: 0.5,
    exactMinFinalScore: 0.78,
    wholesaleMinFinalScore: 0.72,
    strictCategoryMinScore: 0.88,
    strictSubcategoryMinScore: 0.74,
    exactMinVisualScore: 0.8,
    wholesaleMinVisualScore: 0.76,
    similarMinFinalScore: 0.48,
    visualEvidenceMinScore: 0.62,
    brandEvidenceMinScore: 0.55,
    textEvidenceMinScore: 0.55,
    sellerPatternEvidenceMinScore: 0.55,
    priceEvidenceMinScore: 0.55,
    minSupportingEvidenceCount: 3
} as const;

function clampScore(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(1, Math.round(value * 1000) / 1000));
}

function boolCount(values: boolean[]): number {
    return values.filter(Boolean).length;
}

export function weightedSearchRankingScore(input: SearchRankingScoreComponents): number {
    return clampScore(
        input.visualScore * searchRankingScoreWeights.visualScore +
        input.categoryScore * searchRankingScoreWeights.categoryScore +
        input.subcategoryScore * searchRankingScoreWeights.subcategoryScore +
        input.brandScore * searchRankingScoreWeights.brandScore +
        input.textScore * searchRankingScoreWeights.textScore +
        input.sellerPatternScore * searchRankingScoreWeights.sellerPatternScore +
        input.priceEvidenceScore * searchRankingScoreWeights.priceEvidenceScore
    );
}

function cappedFinalScore(input: SearchRankingEvidenceInput): number {
    let score = weightedSearchRankingScore(input);

    if (!input.categoryMatched) score = Math.min(score, 0.42);
    if (input.subcategoryMatched === false) score = Math.min(score, 0.59);
    if (input.visualScore < searchRankingThresholds.visualEvidenceMinScore) score = Math.min(score, 0.58);

    return clampScore(score);
}

function supportingEvidenceCount(input: SearchRankingEvidenceInput): number {
    return boolCount([
        input.visualScore >= searchRankingThresholds.visualEvidenceMinScore,
        input.categoryMatched && input.categoryScore >= searchRankingThresholds.strictCategoryMinScore,
        input.subcategoryMatched === null || input.subcategoryScore >= searchRankingThresholds.strictSubcategoryMinScore,
        input.hasBrandEvidence || input.brandScore >= searchRankingThresholds.brandEvidenceMinScore,
        input.hasTextEvidence || input.textScore >= searchRankingThresholds.textEvidenceMinScore,
        input.hasSellerPatternEvidence || input.sellerPatternScore >= searchRankingThresholds.sellerPatternEvidenceMinScore,
        input.hasPriceEvidence || input.priceEvidenceScore >= searchRankingThresholds.priceEvidenceMinScore
    ]);
}

function hasIdentityEvidence(input: SearchRankingEvidenceInput): boolean {
    if (input.requestedSection === 'brand_exact') {
        return input.hasBrandEvidence || input.hasTextEvidence ||
            input.brandScore >= searchRankingThresholds.brandEvidenceMinScore ||
            input.textScore >= searchRankingThresholds.textEvidenceMinScore;
    }

    if (input.requestedSection === 'wholesale_same') {
        return input.hasBrandEvidence || input.hasTextEvidence || input.hasSellerPatternEvidence || input.hasPriceEvidence ||
            input.brandScore >= searchRankingThresholds.brandEvidenceMinScore ||
            input.textScore >= searchRankingThresholds.textEvidenceMinScore ||
            input.sellerPatternScore >= searchRankingThresholds.sellerPatternEvidenceMinScore ||
            input.priceEvidenceScore >= searchRankingThresholds.priceEvidenceMinScore;
    }

    return true;
}

function canUseStrictSection(input: SearchRankingEvidenceInput, finalScore: number): boolean {
    if (input.requestedSection === 'similar_style') return false;
    if (!input.categoryMatched) return false;
    if (input.subcategoryMatched === false) return false;
    if (input.categoryScore < searchRankingThresholds.strictCategoryMinScore) return false;
    if (input.subcategoryMatched === true && input.subcategoryScore < searchRankingThresholds.strictSubcategoryMinScore) return false;
    if (!hasIdentityEvidence(input)) return false;

    if (input.requestedSection === 'brand_exact') {
        return input.visualScore >= searchRankingThresholds.exactMinVisualScore &&
            finalScore >= searchRankingThresholds.exactMinFinalScore;
    }

    return input.visualScore >= searchRankingThresholds.wholesaleMinVisualScore &&
        finalScore >= searchRankingThresholds.wholesaleMinFinalScore;
}

function downgradeReasons(input: SearchRankingEvidenceInput, finalScore: number): string[] {
    const reasons: string[] = [];

    if (!input.categoryMatched) reasons.push('category_mismatch');
    if (input.subcategoryMatched === false) reasons.push('subcategory_mismatch');
    if (input.visualScore < searchRankingThresholds.visualEvidenceMinScore) reasons.push('low_visual_score');
    if (input.requestedSection !== 'similar_style' && !hasIdentityEvidence(input)) reasons.push('missing_identity_evidence');
    if (finalScore < searchRankingThresholds.verificationMinFinalScore) reasons.push('low_final_score');

    return reasons;
}

export function rankSearchCandidate(input: SearchRankingEvidenceInput): SearchRankingResult {
    const normalizedInput: SearchRankingEvidenceInput = {
        ...input,
        visualScore: clampScore(input.visualScore),
        categoryScore: clampScore(input.categoryScore),
        subcategoryScore: clampScore(input.subcategoryScore),
        brandScore: clampScore(input.brandScore),
        textScore: clampScore(input.textScore),
        sellerPatternScore: clampScore(input.sellerPatternScore),
        priceEvidenceScore: clampScore(input.priceEvidenceScore)
    };
    const finalScore = cappedFinalScore(normalizedInput);
    const reasons = downgradeReasons(normalizedInput, finalScore);
    const evidenceCount = supportingEvidenceCount(normalizedInput);
    const section = canUseStrictSection(normalizedInput, finalScore)
        ? normalizedInput.requestedSection
        : 'similar_style';
    const needsVerification = normalizedInput.sourceStatus === 'needs_verification' ||
        finalScore < searchRankingThresholds.verificationMinFinalScore ||
        reasons.length > 0 ||
        evidenceCount < searchRankingThresholds.minSupportingEvidenceCount;

    return {
        ...normalizedInput,
        finalScore,
        section,
        sourceStatus: needsVerification ? 'needs_verification' : normalizedInput.sourceStatus,
        needsVerification,
        supportingEvidenceCount: evidenceCount,
        downgradeReasons: reasons
    };
}
